/**
 * Demo sandbox banner (docs/features/demo-sandbox/plan.md §4.3, §4.6). In a sandbox it replaces the
 * subscription banners (trial countdown, "Choose a plan", "Add card" do not apply: billing is off).
 *
 * - Active sandbox: a thin "sample data only" line. It is the demo's PHI control (no BAA with AWS for
 *   demo), shown on every screen. While a persona is active the persona bar carries it instead.
 * - Ended sandbox (read-only after the trial): what happens next, Extend (once) and Talk to sales.
 * Renders nothing for a normal tenant.
 */
import React, { useState } from 'react';
import { FlaskConical } from 'lucide-react';
import Banner from '../common/Banner';
import { useAuth } from '../../context/authcontext';
import SandboxService from '../../services/demo/sandbox.service';
import { getActivePersona } from '../../utils/sandbox';

export const SANDBOX_NOTICE = 'Demo sandbox: sample data only. Never enter real patient information.';
const SALES_EMAIL = 'sales@pelorahealth.com';

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : 'soon';

const SandboxBanner: React.FC = () => {
  const { user, refreshPermissions } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sandbox = user?.sandbox;
  if (!sandbox) return null;
  const persona = getActivePersona();
  const ended = user?.subscription?.status === 'read_only';

  if (!ended) {
    if (persona) return null; // the persona bar says it
    return (
      <div className="flex items-center justify-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-1 text-xs text-amber-900" role="note" data-testid="sandbox-notice">
        <FlaskConical size={13} aria-hidden="true" /> {SANDBOX_NOTICE}
      </div>
    );
  }

  const extend = async () => {
    setBusy(true);
    setError(null);
    try {
      await SandboxService.extend();
      await refreshPermissions?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not extend the sandbox');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Banner
      variant="warning"
      data-testid="sandbox-ended"
      className="rounded-none border-x-0 border-t-0"
      action={(
        <span className="flex flex-wrap gap-2">
          {sandbox.canExtend && !persona && (
            <button type="button" onClick={extend} disabled={busy}
              className="rounded bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-50">
              {busy ? 'Extending…' : 'Extend 7 days'}
            </button>
          )}
          <a href={`mailto:${SALES_EMAIL}?subject=Pelora%20demo`} className="rounded border border-amber-700 px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100">
            Talk to sales
          </a>
        </span>
      )}
    >
      Your demo sandbox has ended. You can still look around until {formatDate(sandbox.deleteAt)}, when it is deleted.
      {error && <p className="mt-1 text-xs font-medium" role="alert">{error}</p>}
    </Banner>
  );
};

export default SandboxBanner;
